import { compact, uniqBy } from "lodash";

export const DEFAULT_LOCALE = { value: "en-US", text: "English (US)" };

export const CODING_SHOT = {
    canSkipEditing: false,
    imageViewCode: "Coding",
    imageViewDescription: "Coding",
    isUploading: false,
    isSelected: false,
};

export const isDefaultLocale = (s) => {
    return !s.localeCode || s.localeCode === DEFAULT_LOCALE.value;
};

/**
 * An empty slot may be bulk-removed when it holds no photo, is idle, is not the coding slot and is not required
 * (including the package default image view in the default locale).
 */
export const isRemovableEmptySlot = (shot, packageTemplate) => {
    const isEmpty = !shot.key && !shot.uneditedBlobKey && !shot.editedBlobKey;
    const isBusy = shot.isUploading || shot.isTemporary || shot.copyInProgress;
    const isCoding = !shot.imageViewCode || shot.imageViewCode === CODING_SHOT.imageViewCode;
    const isDefaultView = isDefaultLocale(shot) && !!packageTemplate.defaultImageView && shot.imageViewCode === packageTemplate.defaultImageView;
    const isRequired = shot.isRequired || shot.isRequiredWhenAdded || isDefaultView;

    return isEmpty && !isBusy && !isCoding && !isRequired;
};

export const getRemovableEmptySlots = (shotsByLocale, packageTemplate) => {
    return Object.values(shotsByLocale).flatMap((shots) => shots.filter((s) => isRemovableEmptySlot(s, packageTemplate)));
};

export const sortDefaultLocale = (a, b) => {
    const aIsDefaultLocale = isDefaultLocale(a);
    const bIsDefaultLocale = isDefaultLocale(b);

    return Number(bIsDefaultLocale) - Number(aIsDefaultLocale);
};

export const sortShots = (a, b) => {
    const sortOrder =
        !a.sortOrder || a.sortOrder === null
            ? 1
            : !b.sortOrder || b.sortOrder === null
              ? -1
              : a.sortOrder === b.sortOrder
                ? 0
                : a.sortOrder > b.sortOrder
                  ? 1
                  : -1;

    const sortedLocale = sortDefaultLocale(a, b);

    return sortedLocale === 0 ? sortOrder : sortedLocale;
};

export const mergeShotsWithTemplate = (photos, placeholders, selectedLocaleCode) => {
    const mergedPhotos = placeholders.map((placeholder) => {
        const photo = photos.find((s) => s.localeCode === placeholder.localeCode && s.imageViewCode === placeholder.imageViewCode) || {};

        return {
            ...photo,
            ...placeholder,
            localeCode: placeholder.localeCode || selectedLocaleCode,
        };
    });

    const existingKeys = compact(uniqBy(mergedPhotos, "key").map((e) => e.key));
    const stragglerPhotos = photos.reduce((extraShots, p) => {
        if (existingKeys.includes(p.key)) {
            return extraShots;
        }

        extraShots.push(p);

        return extraShots;
    }, []);

    const sortedEcomShots = [...mergedPhotos, ...stragglerPhotos].sort((a, b) => a.sortOrder - b.sortOrder);

    return sortedEcomShots;
};

export const mergePlaceholders = (pageTemplate, packageTemplateByImageViewCode, imageViewsByCode, localeCode, skipAdditional = false) => {
    const pageTemplateDetailed = pageTemplate
        ? pageTemplate.map((item) => {
              const templateReference =
                  packageTemplateByImageViewCode[item.imageViewCode] && item.localeCode === DEFAULT_LOCALE.value
                      ? packageTemplateByImageViewCode[item.imageViewCode]
                      : imageViewsByCode[item.imageViewCode]
                        ? imageViewsByCode[item.imageViewCode]
                        : { ...item };

              return {
                  ...templateReference,
                  localeCode: item.localeCode || DEFAULT_LOCALE.value,
              };
          })
        : [];
    const pageTemplateImageViewCodes = pageTemplateDetailed.map((iv) => iv.imageViewCode);
    const additionalPlaceholders = skipAdditional
        ? []
        : Object.keys(packageTemplateByImageViewCode).reduce((arr, ivc) => {
              if (!pageTemplateImageViewCodes.includes(ivc)) {
                  arr.push({
                      ...packageTemplateByImageViewCode[ivc],
                      localeCode: localeCode || DEFAULT_LOCALE.value,
                  });
              }

              return arr;
          }, []);

    return [...pageTemplateDetailed, ...additionalPlaceholders];
};

export const sortByLocale = (shots) => {
    return shots.reduce(
        (arr, s) => {
            const localeCode = s.localeCode || DEFAULT_LOCALE.value;

            if (!arr.hasOwnProperty(localeCode)) {
                arr[localeCode] = [];
            }

            arr[localeCode].push({ ...s });

            return arr;
        },
        { [DEFAULT_LOCALE.value]: [] }
    );
};

export const sortByKey = (shots, key) => {
    return shots.reduce((arr, s) => {
        const useKey = s[key];

        if (!arr.hasOwnProperty(useKey)) {
            arr[useKey] = [];
        }

        arr[useKey].push({ ...s });

        return arr;
    }, {});
};

export const EDITING_SOURCE_MAP = {
    brightRiver: 2,
    internal: 3,
    skipEditing: 4,
    brightRiverInternalPremium: 5,
};
